
// /**
//  *
//  * States of the sidebar:
//  * - Default
//  * - User has no teams
//  * - Enterprise control panel
//  *
//  *
//  * Data needed for the sidebar:
//  * - Current user
//  *   - Dashboard contexts
//  *   - Role in the current team
//  * - App context
//  * - User's favorite repositories for the team
//  */

import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'

import ActiveUserMixin, { ScaPlanType } from '~/mixins/activeUserMixin'
import ContextMixin from '~/mixins/contextMixin'
import ControlPanelBaseMixin from '~/mixins/control-panel/ControlPanelBaseMixin'
import OwnerDetailMixin from '~/mixins/ownerDetailMixin'
import PlanDetailMixin from '~/mixins/planDetailMixin'
import RepoListMixin from '~/mixins/repoListMixin'

import MigrateToUnifiedBillingMutation from '~/apollo/mutations/owner/migrateToUnifiedBilling.gql'

import { AuthGetterTypes } from '~/store/account/auth'
import { StarredRepoKey, StarredRepoNode } from '~/store/user/active'

// types
import { TeamPerms } from '~/types/permTypes'
import {
  CompanySizeChoices,
  Repository,
  RepositoryKindChoices,
  SlackConnectChannelType,
  TeamMemberRoleChoices
} from '~/types/types'

import { TEAM_PLAN } from '~/components/Billing/V2/v2PlanConfig'
import {
  DAY_IN_MS,
  getLegacyPlanType,
  getTrialWalkthroughUrl,
  isUnifiedBillingPlan,
  isUnifiedTrialPlan,
  LegacyPlanType,
  PERSONAL_TO_TEAM_DOCS_URL,
  TRIAL_PERIOD_DAYS
} from '~/utils/billing'
import { getFirstPostInstallOnboardingStep } from '~/utils/onboardingFlow'
import { containsElement } from '~/utils/ui'

export default defineComponent({
  name: 'MainSidebar',
  mixins: [
    ActiveUserMixin,
    ContextMixin,
    OwnerDetailMixin,
    PlanDetailMixin,
    RepoListMixin,
    ControlPanelBaseMixin
  ],
  data() {
    return {
      isOpen: false,
      showAddRepoModal: false,
      showMigrateModal: false,
      showJoinSlackModal: false,
      trialSlackConnectChannel: null as SlackConnectChannelType | null,
      isMigrating: false,
      isEligibleForMigration: false,
      collapsedSidebar: false,
      toggleCollapsed: false,
      largeScreenSize: 1024,
      collapseFavs: false,
      TRIAL_PERIOD_DAYS
    }
  },
  computed: {
    ...mapGetters('account/auth', {
      isLoggedIn: AuthGetterTypes.GET_LOGGED_IN
    }),
    canViewReports() {
      return this.$gateKeeper.team(TeamPerms.VIEW_REPORTS, this.teamPerms.permission)
    },
    showTeamMembers() {
      if (this.activeDashboardContext && 'type' in this.activeDashboardContext) {
        if (this.activeDashboardContext.type === 'user') {
          return false
        }

        const role = this.activeDashboardContext.role as TeamMemberRoleChoices
        return this.$gateKeeper.team(
          [TeamPerms.MANAGE_TEAM_MEMBERS, TeamPerms.VIEW_TEAM_MEMBERS],
          role
        )
      }
      return false
    },
    showPolicies() {
      if (this.activeDashboardContext && 'type' in this.activeDashboardContext) {
        if (this.activeDashboardContext.type === 'user') {
          return false
        }

        const role = this.activeDashboardContext.role as TeamMemberRoleChoices
        return this.$gateKeeper.team([TeamPerms.MANAGE_POLICIES], role)
      }
      return false
    },
    showTeamSettings() {
      if (this.activeDashboardContext && 'type' in this.activeDashboardContext) {
        if (this.activeDashboardContext.type === 'user') {
          return false
        }
      }

      if (this.activeDashboardContext && 'role' in this.activeDashboardContext) {
        const role = this.activeDashboardContext.role as TeamMemberRoleChoices
        return this.$gateKeeper.team(
          [
            TeamPerms.CHANGE_PLAN,
            TeamPerms.UPDATE_SEATS,
            TeamPerms.UPDATE_BILLING_DETAILS,
            TeamPerms.MANAGE_TEAM_MEMBERS,
            TeamPerms.VIEW_ACCESS_CONTROL_DASHBOARD,
            TeamPerms.DELETE_TEAM_ACCOUNT,
            TeamPerms.VIEW_TEAM_GENERAL_SETTINGS
          ],
          role
        )
      }

      return false
    },
    teamNavOptions() {
      let { provider, owner } = this.$route.params
      provider = provider ?? this.activeProvider
      owner = owner ?? this.activeOwner

      return [
        {
          label: 'Home',
          icon: 'home',
          to: this.$generateRoute([], false, false, provider, owner)
        },
        {
          label: 'Repositories',
          icon: 'layers',
          to: this.$generateRoute(['repos'], false, true, provider, owner)
        },
        {
          label: 'Reports',
          icon: 'pie-chart',
          to: this.$generateRoute(['reports'], false, true, provider, owner),
          isVisible: this.canViewReports
        },
        {
          label: 'Members',
          icon: 'users',
          to: this.$generateRoute(['members'], false, true, provider, owner),
          isVisible: this.showTeamMembers
        },
        {
          label: 'Policies',
          icon: 'policies',
          to: this.$generateRoute(['policies'], false, true, provider, owner),
          isVisible: this.showPolicies
        },
        {
          label: 'Settings',
          icon: 'settings',
          to: this.$generateRoute(['settings'], false, true, provider, owner),
          isVisible: this.showTeamSettings
        },
        {
          label: 'Enterprise Control Panel',
          icon: 'window',
          to: '/control-panel',
          isVisible: this.isViewerSuperadmin
        },
        {
          label: 'Upgrade plan',
          icon: 'zap',
          to: this.$generateRoute(['settings', 'billing'], false, true, provider, owner),
          isVisible:
            this.isUserOnFreePlan &&
            this.isUserAdmin &&
            !this.activeLink(
              this.$generateRoute(['settings', 'billing'], false, true, provider, owner)
            )
        }
      ]
    },
    modalStyle() {
      if (this.collapsedSidebar) {
        return 'w-80 md:w-13'
      }
      if (this.isOpen) {
        return 'w-80 lg:left-0 left-0'
      }
      return 'w-80 lg:left-0 -left-full'
    },
    currentYear() {
      return new Date().getFullYear()
    },
    installationUrl() {
      if (
        this.viewer.connectedVcsProviders?.length &&
        this.viewer.connectedVcsProviders.length > 1
      ) {
        return '/installation/providers'
      }
      const provider = this.viewer.connectedVcsProviders?.[0]
      return provider
        ? this.installationUrls[this.$providerMetaMap[provider].value]
        : '/installation/providers'
    },
    firstPostInstallStepRoute() {
      const firstStep = getFirstPostInstallOnboardingStep()
      if (!firstStep) {
        return '/installation/providers'
      }
      const provider = this.activeProvider || this.$route.params.provider
      const owner = this.activeOwner || this.$route.params.owner
      if (!provider || !owner) {
        return '/installation/providers'
      }
      return {
        name: firstStep.routeName,
        params: {
          provider,
          owner
        }
      }
    },
    provider(): string {
      return this.activeProvider
    },
    isUserAdmin() {
      return (
        this.$gateKeeper.team(TeamPerms.UPDATE_BILLING_DETAILS, this.teamPerms.permission) &&
        this.activeDashboardContext.type !== 'user'
      )
    },
    isPersonalAccount(): boolean {
      return this.activeDashboardContext?.type === 'user'
    },
    personalToTeamDocsUrl(): string {
      return PERSONAL_TO_TEAM_DOCS_URL
    },
    quotaExhaustedBody(): string {
      if (this.isPersonalAccount) {
        return 'You have exhausted your usage quota for this month. Switch to a team account to keep things running.'
      }
      return 'You have exhausted your usage quota for this month. Upgrade your plan to keep things running.'
    },
    canInviteMembers(): boolean {
      return this.$gateKeeper.team(TeamPerms.MANAGE_TEAM_MEMBERS, this.teamPerms.permission)
    },
    isUnifiedBilling(): boolean {
      return isUnifiedBillingPlan(this.activeDashboardContext?.subscribed_plan_info)
    },
    showQuotaExhausted() {
      if (this.isUnifiedBilling) return false
      return (
        this.owner.maxUsagePercentage &&
        this.owner.maxUsagePercentage >= 100 &&
        (Object.keys(this.availableUpgradePlans).length ||
          // "free" isn't a slug on Asgard. SMH
          this.ownerBillingInfo.planSlug === 'free')
      )
    },
    isUserOnFreePlan() {
      if (this.isUnifiedBilling) return false
      return this.ownerBillingInfo.planSlug === 'free'
    },
    activeOwnerProvider(): StarredRepoKey | null {
      if (this.activeOwner && this.activeProvider) {
        return `${this.activeOwner}::${this.$providerMetaMap[this.activeProvider].value}`
      }
      return null
    },
    favRepos() {
      return this.activeOwnerProvider
        ? (this.viewerStarredRepos.get(this.activeOwnerProvider) ?? [])
        : []
    },
    isOnFreeSCAPlan() {
      if (this.isUnifiedBilling) return false
      return (
        this.isUserAdmin &&
        typeof this.activeDashboardContext.sca_plan_info === 'object' &&
        this.activeDashboardContext.sca_plan_info?.plan === ScaPlanType.Free
      )
    },
    legacyPlanType(): LegacyPlanType | null {
      return getLegacyPlanType(this.activeDashboardContext?.subscribed_plan_info)
    },
    isFreePlan(): boolean {
      return this.legacyPlanType === LegacyPlanType.Free
    },
    showWhatsNewCard(): boolean {
      return (this.isFreePlan || this.isEligibleForMigration) && this.isUserAdmin
    },
    isV1StarterPlan(): boolean {
      if (this.activeDashboardContext?.type !== 'team') return false
      return this.legacyPlanType === LegacyPlanType.Starter
    },
    whatsNewButtonText(): string {
      return this.legacyPlanType === LegacyPlanType.Business
        ? 'Migrate to Team plan'
        : 'Upgrade to Team plan'
    },
    currentPlanPriceLabel(): string {
      const isAnnual = this.owner?.billingInfo?.planSlug?.includes('annual')
      if (this.isV1StarterPlan) {
        return isAnnual ? '$8/seat/mo, billed annually' : '$10/seat/mo, billed monthly'
      }
      const price = isAnnual ? TEAM_PLAN.annualPrice : TEAM_PLAN.price
      return isAnnual ? `${price}, billed annually` : `${price}, billed monthly`
    },
    newPlanPriceLabel(): string {
      const isAnnual = this.owner?.billingInfo?.planSlug?.includes('annual')
      if (this.isV1StarterPlan) {
        return isAnnual
          ? '$24/contributor/mo, billed annually'
          : '$30/contributor/mo, billed monthly'
      }
      const price = isAnnual ? TEAM_PLAN.annualPrice : TEAM_PLAN.price
      return isAnnual ? `${price}, billed annually` : `${price}, billed monthly`
    },
    showTrialCard(): boolean {
      if (!this.activeDashboardContext) {
        return false
      }

      // Only show for teams (not personal accounts)
      if (this.activeDashboardContext.type === 'user') {
        return false
      }

      if (!this.isUserAdmin) {
        return false
      }

      // Check if the plan slug indicates a trial (utr = unified trial)
      const planInfo = this.activeDashboardContext.subscribed_plan_info
      return isUnifiedTrialPlan(planInfo)
    },
    trialDaysRemaining(): number {
      // For unified trial plans, use trialEndsAt from productSubscription
      let trialEndsAt = this.owner?.productSubscription?.billingDetails?.trialEndsAt

      // Fallback: if trialEndsAt is null, use createdAt + 14 days
      if (!trialEndsAt) {
        const createdAt = this.owner?.productSubscription?.product?.createdAt

        if (createdAt) {
          const createdDate = new Date(createdAt)
          if (isNaN(createdDate.getTime())) {
            return TRIAL_PERIOD_DAYS
          }
          const trialEndDate = new Date(createdDate.getTime() + TRIAL_PERIOD_DAYS * DAY_IN_MS)
          trialEndsAt = trialEndDate.toISOString()
        } else {
          return TRIAL_PERIOD_DAYS
        }
      }

      const trialEndDate = new Date(trialEndsAt)
      if (isNaN(trialEndDate.getTime())) {
        return TRIAL_PERIOD_DAYS
      }

      const today = new Date()
      const diffTime = trialEndDate.getTime() - today.getTime()
      const diffDays = Math.ceil(diffTime / DAY_IN_MS)

      return Math.max(0, diffDays)
    },
    trialWalkthroughUrl(): string {
      return getTrialWalkthroughUrl(
        this.$config.trialWalkthroughUrl,
        this.viewer?.profile?.companySize
      )
    },
    showSlackConnect(): boolean {
      const companySize = this.viewer?.profile?.companySize
      return (
        Boolean(companySize) &&
        companySize !== CompanySizeChoices.UpTo_5 &&
        companySize !== CompanySizeChoices.Size_5_25
      )
    }
  },
  created() {
    this.collapsedSidebar = Boolean(this.$nuxt.$cookies.get('ui-state-sidebar-collapsed'))
  },
  async fetch() {
    await Promise.allSettled([this.fetchContext(), this.fetchActiveUser()])

    await Promise.all([
      this.fetchStarredRepos({
        login: this.activeOwner,
        provider: this.$providerMetaMap[this.activeProvider].value
      }),
      this.fetchBillingOrTrialData(this.activeOwner, this.activeProvider),
      ...(this.$config.onPrem ? [this.fetchIsViewerSuperadmin()] : [])
    ])
  },
  mounted() {
    this.$root.$on('ui:show-sidebar-menu', () => {
      this.collapsedSidebar = false
      this.isOpen = true
    })

    const login = this.activeOwner
    const provider = this.activeProvider

    if (login && provider) {
      this.fetchMaxUsagePercentage({ login, provider })
    }

    this.checkMigrationEligibility()
    this.$root.$on('billing:plan-changed', this.checkMigrationEligibility)
  },
  beforeDestroy() {
    this.$root.$off('ui:show-sidebar-menu')
    this.$root.$off('billing:plan-changed', this.checkMigrationEligibility)
  },
  watch: {
    activeOwnerProvider: 'fetchRouteUpdates',
    isOpen: 'disableScroll',
    'owner.id'() {
      this.checkMigrationEligibility()
    }
  },
  methods: {
    activeLink(to: string): boolean {
      return this.$route.path.startsWith(to)
    },
    fetchRouteUpdates() {
      const login = this.activeOwner
      const provider = this.activeProvider

      this.isEligibleForMigration = false

      if (login && provider) {
        this.fetchStarredRepos({
          login,
          provider: this.$providerMetaMap[this.activeProvider].value
        })
        this.fetchMaxUsageInfo()
        this.fetchBillingOrTrialData(login, provider)
      }
    },
    getIcon({ isPrivate, kind }: Pick<Repository, 'isPrivate' | 'kind'>): string {
      if (kind === RepositoryKindChoices.Subrepo) {
        return isPrivate ? 'folder-locked' : 'folder-globe'
      }

      return isPrivate ? 'z-lock' : 'globe'
    },
    closeMenu(event: Event) {
      const target = event.target as HTMLElement
      const toggleButton = document.getElementById('mobile-menu-toggle')
      if (!toggleButton) {
        this.isOpen = false
      } else if (!containsElement(toggleButton, target) && target.id !== 'mobile-menu-toggle') {
        this.isOpen = false
      }
    },
    disableScroll(newIsOpen: boolean) {
      if (newIsOpen && process.client) {
        document.body.classList.add('no-scroll')
      } else if (process.client) {
        document.body.classList.remove('no-scroll')
      }
    },
    async handleSlackInviteSent(): Promise<void> {
      this.showJoinSlackModal = false
      await this.fetchTrialInfo({
        login: this.activeOwner,
        provider: this.activeProvider,
        refetch: true
      })
      this.trialSlackConnectChannel = this.owner?.team?.slackConnectChannel ?? null
    },
    fetchBillingOrTrialData(login: string, provider: string) {
      const planInfo = this.activeDashboardContext?.subscribed_plan_info
      const isUnifiedTrial = isUnifiedTrialPlan(planInfo)

      if (isUnifiedTrial && this.isUserAdmin) {
        return this.fetchTrialInfo({ login, provider }).then(() => {
          this.trialSlackConnectChannel = this.owner?.team?.slackConnectChannel ?? null
          if (!this.owner?.productSubscription?.billingDetails?.trialEndsAt) {
            this.$sentry.captureMessage(
              'Trial card: Missing trialEndsAt, using createdAt fallback',
              { level: 'info' }
            )
          }
        })
      } else if (isUnifiedTrial || this.isUnifiedBilling) {
        return Promise.resolve()
      } else {
        return this.fetchBillingDetails({ login, provider })
      }
    },
    fetchMaxUsageInfo() {
      const params = { login: this.activeOwner, provider: this.activeProvider, refetch: true }
      this.fetchMaxUsagePercentage(params)

      if (!this.$config.onPrem && process.client && this.isLoggedIn) {
        const { analyticsId, email, firstName, lastName } = this.viewer

        if (analyticsId && email) {
          this.$sentry.setUser({
            email,
            id: analyticsId
          })

          this.$analytics.identify(analyticsId, {
            email,
            firstName,
            lastName
          })

          this.$trackingAnalytics?.identify(analyticsId, {
            email,
            firstName,
            lastName
          })
        }
      }
    },
    async checkMigrationEligibility(): Promise<void> {
      if (this.$config.onPrem) return
      // Free plans skip the migration check entirely — they don't need it and this avoids an unnecessary API call
      if (this.isFreePlan) return
      if (this.activeDashboardContext?.type !== 'team' || !this.owner?.id) return
      try {
        const response = await this.$applyGraphqlMutation(MigrateToUnifiedBillingMutation, {
          ownerId: this.owner.id,
          confirm: false
        })
        this.isEligibleForMigration = Boolean(response?.data?.migrateToUnifiedBilling?.ok)
      } catch (e) {
        this.isEligibleForMigration = false
        this.$sentry.captureException(e as Error, {
          tags: {
            feature: 'team-plan-migration'
          },
          extra: {
            ownerId: this.owner.id
          }
        })
      }
    },
    handleWhatsNewCta() {
      if (this.isFreePlan) {
        this.$router.push(this.$generateRoute(['settings', 'billing', 'checkout'], false, true))
      } else {
        this.showMigrateModal = true
      }
    },
    async handleMigratePlan(): Promise<void> {
      this.isMigrating = true
      try {
        const response = await this.$applyGraphqlMutation(MigrateToUnifiedBillingMutation, {
          ownerId: this.owner.id,
          confirm: true
        })

        const result = response?.data?.migrateToUnifiedBilling
        if (result?.ok) {
          this.$toast.success('Successfully migrated to the Team plan.')
          this.showMigrateModal = false
          this.isEligibleForMigration = false
          await Promise.all([
            this.refetchUser(),
            this.fetchBillingDetails({
              login: this.activeOwner,
              provider: this.activeProvider,
              refetch: true
            })
          ])
        } else {
          this.$toast.danger(result?.error || 'Something went wrong while migrating your plan.')
        }
      } catch (e: unknown) {
        this.$logErrorAndToast(e as Error, 'Something went wrong while migrating your plan.')
      } finally {
        this.isMigrating = false
      }
    },
    getFavRepoProps(repo: StarredRepoNode) {
      const provider = this.$providerMetaMap[repo.vcsProvider].shortcode
      return {
        label: repo.displayName,
        icon: this.getIcon(repo),
        to: this.$generateRoute(
          [repo.name],
          false,
          false,
          provider,
          repo.ownerLogin ?? this.activeOwner
        )
      }
    }
  }
})
